import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, UserSession, ResearchProject, UserProfile, UserPreferences, KoboAccount, KoboAccountFormData, ProjectKoboContext } from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Get API URL from environment or use default
    this.baseURL = import.meta.env.VITE_API_URL || 'https://api.research-automation.local';
    
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include session token
    this.api.interceptors.request.use((config) => {
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId) {
        config.headers.Authorization = `Bearer ${sessionId}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear session on unauthorized
          localStorage.removeItem('sessionId');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication Methods
  async authenticateWithGoogle(googleToken: string): Promise<ApiResponse<UserSession>> {
    try {
      const response: AxiosResponse<ApiResponse<UserSession>> = await this.api.post('/auth/google', {
        id_token: googleToken,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Authentication failed',
      };
    }
  }

  async validateSession(): Promise<ApiResponse<UserSession>> {
    try {
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        return {
          success: false,
          error: 'No session ID found',
        };
      }

      const response: AxiosResponse<any> = await this.api.post('/auth/validate', {
        session_id: sessionId,
      });
      
      // Transform the backend response to match frontend expectations
      if (response.data.valid) {
        return {
          success: true,
          data: {
            sessionId: response.data.session.session_id,
            user: {
              id: response.data.user_profile.user_id,
              email: response.data.user_profile.email,
              name: response.data.user_profile.name,
              status: response.data.user_profile.status,
              isSuperAdmin: response.data.user_profile.isSuperAdmin,
            },
            expiresAt: response.data.session.expires_at,
          },
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Session validation failed',
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Session validation failed',
      };
    }
  }

  async logout(): Promise<ApiResponse> {
    try {
      const sessionId = localStorage.getItem('sessionId');
      const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/logout', {
        session_id: sessionId,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Logout failed',
      };
    }
  }

  // Project Methods
  async getProjects(koboAccountId?: string): Promise<ApiResponse<ResearchProject[]>> {
    try {
      const params = koboAccountId ? { kobo_account_id: koboAccountId } : {};
      const response: AxiosResponse<any> = await this.api.get('/projects', { params });
      
      if (response.data.success && response.data.projects) {
        // Transform backend projects to frontend format
        const transformedProjects = response.data.projects.map((project: any) => ({
          projectId: project.project_id,
          title: project.title,
          description: project.description,
          createdBy: project.created_by,
          createdAt: project.created_at,
          updatedAt: project.updated_at,
          status: project.status,
          koboAccountId: project.kobo_account_id,
          torDocument: project.tor_document,
          indicators: project.indicators,
          questionnaire: project.questionnaire,
          koboFormId: project.kobo_form_id,
          koboFormUrl: project.kobo_form_url,
        }));
        
        return {
          success: true,
          data: transformedProjects,
        };
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch projects',
      };
    }
  }

  async createProject(projectData: Partial<ResearchProject>, koboAccountId: string): Promise<ApiResponse<ResearchProject>> {
    try {
      const requestData = {
        ...projectData,
        kobo_account_id: koboAccountId,
      };
      
      const response: AxiosResponse<any> = await this.api.post('/projects', requestData);
      
      if (response.data.success && response.data.project) {
        // Transform backend project to frontend format
        const transformedProject = {
          projectId: response.data.project.project_id,
          title: response.data.project.title,
          description: response.data.project.description,
          createdBy: response.data.project.created_by,
          createdAt: response.data.project.created_at,
          updatedAt: response.data.project.updated_at,
          status: response.data.project.status,
          koboAccountId: response.data.project.kobo_account_id,
          torDocument: response.data.project.tor_document,
          indicators: response.data.project.indicators,
          questionnaire: response.data.project.questionnaire,
          koboFormId: response.data.project.kobo_form_id,
          koboFormUrl: response.data.project.kobo_form_url,
        };
        
        return {
          success: true,
          data: transformedProject,
        };
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create project',
      };
    }
  }

  async getProject(projectId: string): Promise<ApiResponse<ResearchProject>> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/projects/${projectId}`);
      
      if (response.data.success && response.data.project) {
        const project = response.data.project;
        
        // Extract indicators from nested data structure
        let indicators = null;
        if (project.data?.indicators?.data?.indicators) {
          indicators = project.data.indicators.data.indicators;
        } else if (project.indicators) {
          indicators = project.indicators;
        }
        
        // Extract questionnaire from nested data structure
        let questionnaire = null;
        if (project.data?.questionnaire?.data?.questionnaire) {
          questionnaire = project.data.questionnaire.data.questionnaire;
        } else if (project.questionnaire) {
          questionnaire = project.questionnaire;
        }
        
        // Transform backend project to frontend format
        const transformedProject = {
          projectId: project.project_id,
          title: project.title,
          description: project.description,
          createdBy: project.created_by,
          createdAt: project.created_at,
          updatedAt: project.updated_at,
          status: project.status,
          koboAccountId: project.kobo_account_id,
          torDocument: project.tor_document,
          indicators: indicators,
          questionnaire: questionnaire,
          koboFormId: project.kobo_form_id,
          koboFormUrl: project.kobo_form_url,
        };
        
        return {
          success: true,
          data: transformedProject,
        };
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch project',
      };
    }
  }

  async updateProjectKoboAccount(projectId: string, koboAccountId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.put(`/projects/${projectId}`, {
        kobo_account_id: koboAccountId,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update project KoboToolbox account',
      };
    }
  }

  async getProjectKoboContext(projectId: string): Promise<ApiResponse<ProjectKoboContext>> {
    try {
      const response: AxiosResponse<any> = await this.api.get(`/projects/${projectId}/kobo-context`);
      
      // Backend returns project data, we need to extract Kobo context
      if (response.data.success && response.data.project) {
        const project = response.data.project;
        
        // Try to get account name from user profile
        let accountName = 'KoboToolbox Account';
        try {
          const profileResponse = await this.getUserProfile();
          if (profileResponse.success && profileResponse.data?.kobo_accounts) {
            const account = profileResponse.data.kobo_accounts.find(
              (acc: any) => acc.account_id === project.kobo_account_id
            );
            if (account) {
              accountName = account.account_name;
            }
          }
        } catch (err) {
          console.warn('Could not fetch account name:', err);
        }
        
        // Extract Kobo context from project data
        const koboContext: ProjectKoboContext = {
          project_id: project.project_id,
          kobo_account_id: project.kobo_account_id || '',
          account_name: accountName,
          server_url: project.kobo_form_url ? new URL(project.kobo_form_url).origin : 'https://kf.kobotoolbox.org',
          deployment_status: project.status === 'kobo_deployed' ? 'deployed' : 'not_deployed',
          form_id: project.kobo_form_id || undefined,
          form_url: project.kobo_form_url || undefined,
          last_sync: project.updated_at || undefined,
          account_active: !!project.kobo_account_id
        };
        
        return {
          success: true,
          data: koboContext,
        };
      }
      
      // Fallback for old API format
      if (response.data.success && response.data.context) {
        return {
          success: true,
          data: response.data.context,
        };
      }
      
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch project KoboToolbox context',
      };
    }
  }

  async updateProject(projectId: string, projectData: Partial<ResearchProject>): Promise<ApiResponse<ResearchProject>> {
    try {
      // Transform frontend format to backend format
      const backendData: any = {};
      
      if (projectData.title !== undefined) {
        backendData.title = projectData.title;
      }
      if (projectData.description !== undefined) {
        backendData.description = projectData.description;
      }
      if (projectData.koboAccountId !== undefined) {
        backendData.kobo_account_id = projectData.koboAccountId;
      }
      
      const response: AxiosResponse<ApiResponse<ResearchProject>> = await this.api.put(`/projects/${projectId}`, backendData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update project',
      };
    }
  }

  async deleteProject(projectId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.delete(`/projects/${projectId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete project',
      };
    }
  }

  // File Upload Methods
  async uploadTorDocument(projectId: string, file: File): Promise<ApiResponse<{ documentId: string }>> {
    try {
      // Convert file to base64 for JSON transport
      const fileContent = await this.fileToBase64(file);
      
      const requestData = {
        file_data: {
          content: fileContent,
          filename: file.name,
          content_type: file.type
        }
      };

      const response: AxiosResponse<ApiResponse<{ documentId: string }>> = await this.api.post(
        `/projects/${projectId}/tor-upload`,
        requestData
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload TOR document',
      };
    }
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }



  // Asynchronous Workflow Methods (Recommended)
  async startWorkflow(projectId: string, workflowConfig?: any): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    try {
      const response: AxiosResponse<{ success: boolean; execution_arn?: string; workflow_id?: string; status?: string; error?: string; message?: string }> = await this.api.post(
        `/projects/${projectId}/workflow/start`,
        { workflow_config: workflowConfig || {} }
      );
      
      // The backend returns data directly, not wrapped in ApiResponse format
      if (response.data.success) {
        return {
          success: true,
          data: {
            execution_arn: response.data.execution_arn!,
            workflow_id: response.data.workflow_id!,
            status: response.data.status!
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to start workflow'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to start workflow',
      };
    }
  }

  async getWorkflowStatus(projectId: string, executionArn: string): Promise<ApiResponse<{ 
    status: string; 
    started_at?: string;
    ended_at?: string;
    current_step?: string; 
    output?: any; 
    error?: string;
    cause?: string;
  }>> {
    try {
      // URL encode the execution ARN
      const encodedArn = encodeURIComponent(executionArn);
      const response: AxiosResponse<ApiResponse<{ 
        status: string; 
        started_at?: string;
        ended_at?: string;
        current_step?: string; 
        output?: any; 
        error?: string;
        cause?: string;
      }>> = await this.api.get(
        `/projects/${projectId}/workflow/status/${encodedArn}`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get workflow status',
      };
    }
  }

  async listProjectWorkflows(projectId: string): Promise<ApiResponse<{ workflows: any[]; count: number }>> {
    try {
      const response: AxiosResponse<{ success: boolean; workflows?: any[]; count?: number; error?: string }> = await this.api.get(
        `/projects/${projectId}/workflow`
      );
      
      // The backend returns data directly, not wrapped in ApiResponse format
      if (response.data.success) {
        return {
          success: true,
          data: {
            workflows: response.data.workflows || [],
            count: response.data.count || 0
          }
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to list workflows'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to list workflows',
      };
    }
  }

  // Enhanced Workflow Management
  async startFullWorkflow(projectId: string, config?: {
    questionnaire_config?: any;
    deployment_config?: any;
  }): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    return this.startWorkflow(projectId, {
      workflow_type: 'full_workflow',
      ...config
    });
  }

  async startIndicatorExtraction(projectId: string): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    return this.startWorkflow(projectId, {
      workflow_type: 'extract_indicators_only'
    });
  }

  async startQuestionnaireDesign(projectId: string, config?: any): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    return this.startWorkflow(projectId, {
      workflow_type: 'design_questionnaire_only',
      questionnaire_config: config
    });
  }

  async startKoboDeployment(projectId: string, config?: any): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    return this.startWorkflow(projectId, {
      workflow_type: 'deploy_kobo_only',
      deployment_config: config
    });
  }

  // Workflow Control Methods
  async pauseWorkflow(projectId: string, executionArn: string): Promise<ApiResponse> {
    try {
      const encodedArn = encodeURIComponent(executionArn);
      const response: AxiosResponse<ApiResponse> = await this.api.post(
        `/projects/${projectId}/workflow/${encodedArn}/pause`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to pause workflow',
      };
    }
  }

  async resumeWorkflow(projectId: string, executionArn: string): Promise<ApiResponse> {
    try {
      const encodedArn = encodeURIComponent(executionArn);
      const response: AxiosResponse<ApiResponse> = await this.api.post(
        `/projects/${projectId}/workflow/${encodedArn}/resume`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resume workflow',
      };
    }
  }

  async cancelWorkflow(projectId: string, executionArn: string): Promise<ApiResponse> {
    try {
      const encodedArn = encodeURIComponent(executionArn);
      const response: AxiosResponse<ApiResponse> = await this.api.post(
        `/projects/${projectId}/workflow/${encodedArn}/cancel`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to cancel workflow',
      };
    }
  }

  async retryWorkflow(projectId: string, executionArn: string): Promise<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> {
    try {
      const encodedArn = encodeURIComponent(executionArn);
      const response: AxiosResponse<ApiResponse<{ execution_arn: string; workflow_id: string; status: string }>> = await this.api.post(
        `/projects/${projectId}/workflow/${encodedArn}/retry`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retry workflow',
      };
    }
  }

  // Resume workflow from last checkpoint (idempotent)
  async resumeWorkflowFromCheckpoint(projectId: string, forceStep?: 'extract' | 'design' | 'deploy'): Promise<ApiResponse<{
    step: string;
    message: string;
    result: any;
    next_step?: string;
    completed?: boolean;
  }>> {
    try {
      const requestData = forceStep ? { force_step: forceStep } : {};
      const response: AxiosResponse<ApiResponse<{
        step: string;
        message: string;
        result: any;
        next_step?: string;
        completed?: boolean;
      }>> = await this.api.post(
        `/projects/${projectId}/workflow/resume`,
        requestData
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resume workflow',
      };
    }
  }

  // User Profile Methods
  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    try {
      const response: AxiosResponse<any> = await this.api.get('/profile');
      
      // Transform backend response to match frontend expectations
      if (response.data.success && response.data.profile) {
        return {
          success: true,
          data: response.data.profile,
        };
      }
      
      return {
        success: false,
        error: response.data.error || 'Failed to fetch user profile',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch user profile',
      };
    }
  }

  async updateUserProfile(profileData: Partial<UserProfile>): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.put('/profile', profileData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user profile',
      };
    }
  }

  async updateUserPreferences(preferences: UserPreferences): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.put('/profile/preferences', {
        preferences,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user preferences',
      };
    }
  }

  async addKoboAccount(accountData: KoboAccountFormData): Promise<ApiResponse<KoboAccount>> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/profile/kobo-accounts', accountData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add KoboToolbox account',
        validation_errors: error.response?.data?.validation_errors,
      } as ApiResponse<KoboAccount>;
    }
  }

  async updateKoboAccount(accountId: string, accountData: Partial<KoboAccountFormData>): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.put(`/profile/kobo-accounts/${accountId}`, accountData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update KoboToolbox account',
        validation_errors: error.response?.data?.validation_errors,
      } as ApiResponse<any>;
    }
  }

  async deleteKoboAccount(accountId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.delete(`/profile/kobo-accounts/${accountId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete KoboToolbox account',
      };
    }
  }

  async validateKoboAccount(accountId: string): Promise<ApiResponse<{ validation_result: any }>> {
    try {
      const response: AxiosResponse<any> = await this.api.post(`/profile/kobo-accounts/${accountId}/validate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to validate KoboToolbox account',
      };
    }
  }

  async setDefaultKoboAccount(accountId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post(`/profile/kobo-accounts/${accountId}/set-default`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to set default KoboToolbox account',
      };
    }
  }

  // TOR Builder Agent Methods
  async invokeTORBuilder(request: { prompt: string; project_id?: string }): Promise<ApiResponse<{ response: string; tor_progress?: any }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ response: string; tor_progress?: any }>> = await this.api.post(
        '/agents/tor-builder/invoke',
        request
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to invoke TOR Builder',
      };
    }
  }

  async saveTORAndStart(request: { tor_content: string; project_id: string }): Promise<ApiResponse<{ project_status: string; ready_for_workflow: boolean }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ project_status: string; ready_for_workflow: boolean }>> = await this.api.post(
        '/agents/tor-builder/save-and-start',
        request
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save TOR and start workflow',
      };
    }
  }

  // Admin User Management Methods
  async listAllUsers(statusFilter?: 'pending' | 'approved' | 'suspended'): Promise<ApiResponse<{ users: any[]; count: number }>> {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response: AxiosResponse<any> = await this.api.get('/admin/users', { params });
      
      // Backend returns data directly in response.data
      if (response.data.success) {
        return {
          success: true,
          data: {
            users: response.data.users || [],
            count: response.data.count || 0
          }
        };
      }
      
      return {
        success: false,
        error: response.data.error || 'Failed to fetch users',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch users',
      };
    }
  }

  async approveUser(userId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post(`/admin/users/${userId}/approve`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to approve user',
      };
    }
  }

  async suspendUser(userId: string, reason: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post(`/admin/users/${userId}/suspend`, {
        reason,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to suspend user',
      };
    }
  }

  async reactivateUser(userId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post(`/admin/users/${userId}/reactivate`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reactivate user',
      };
    }
  }
}

export const apiService = new ApiService();